import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { PublicShell } from "@/components/public/public-shell";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ArrowLeft, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { SECTIONS } from "@/data/bousala-v1.config";
import { getItem, type FlatItem } from "@/lib/bousala/items";
import { asLang, dirFor, tItem, tSection, type Lang } from "@/lib/bousala/i18n";
import {
  LikertField,
  ScenarioField,
  Top3Field,
  serializeAnswer,
} from "@/components/bousala/item-widgets";
import {
  getRepairableAttempt,
  getAttemptAnsweredIds,
  saveAnswer,
  submitAttempt,
} from "@/lib/bousala.functions";
import { generateBousalaReport, getBousalaReport } from "@/lib/bousala/report.functions";
import { ReportPreparing } from "@/components/bousala/report-preparing";
import { useScreenFocus } from "@/lib/use-screen-focus";
import { userErrorMessage } from "@/lib/user-error";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/bousala/repair")({
  head: () => ({
    meta: [
      { title: "بوصلة د. سارة — استكمال تقريرك" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BousalaRepairInShell,
});

/** Order the missing ids the way the test itself delivers them. */
function orderMissing(ids: string[]): FlatItem[] {
  const set = new Set(ids);
  const out: FlatItem[] = [];
  for (const s of SECTIONS) {
    for (const it of s.items) {
      if (set.has(it.id)) {
        const flat = getItem(it.id);
        if (flat) out.push(flat);
      }
    }
  }
  return out;
}

type Phase = "questions" | "verifying" | "preparing";

function BousalaRepair() {
  const navigate = useNavigate();
  const fnRepairable = useServerFn(getRepairableAttempt);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["bousala", "repairable"],
    queryFn: () => fnRepairable(),
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div dir="rtl" className="mx-auto max-w-2xl px-5 py-16 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  if (!data) return <NothingToRepair />;

  return <RepairRunner key={data.attemptId} data={data} onRefresh={() => void refetch()} navigateHome={() => navigate({ to: "/portal" })} />;
}

function NothingToRepair() {
  return (
    <div dir="rtl" className="mx-auto max-w-2xl px-5 py-14 md:py-20">
      <div className="rounded-ds-xl bg-surface p-8 text-center shadow-sm md:p-10">
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Heart className="h-7 w-7 text-primary" aria-hidden="true" />
        </div>
        <h1 className="mt-4 font-display text-2xl font-extrabold text-foreground">
          لا شيء بانتظارك هنا
        </h1>
        <p className="mt-3 text-base leading-loose text-foreground/80">
          لا يوجد تقرير بحاجة إلى استكمال في حسابك حاليًا. يمكنك متابعة كل شيء من لوحتك.
        </p>
        <Link
          to="/portal"
          className="mt-8 inline-flex items-center justify-center rounded-full bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-sm transition hover:opacity-95"
        >
          الذهاب إلى لوحتي
        </Link>
      </div>
    </div>
  );
}

function RepairRunner({
  data,
  onRefresh,
  navigateHome,
}: {
  data: { attemptId: string; language: "ar" | "en"; missingIds: string[] };
  onRefresh: () => void;
  navigateHome: () => void;
}) {
  const navigate = useNavigate();
  const lang: Lang = asLang(data.language);
  const dir = dirFor(lang);
  const attemptId = data.attemptId;

  const [queue, setQueue] = useState<FlatItem[]>(() => orderMissing(data.missingIds));
  const [pos, setPos] = useState(0);
  const [draft, setDraft] = useState<unknown>(undefined);
  const [saving, setSaving] = useState(false);
  const [phase, setPhase] = useState<Phase>("questions");
  const [stillMissing, setStillMissing] = useState<number | null>(null);
  const shownAt = useRef<number>(Date.now());
  const preparingStartedAt = useRef<number>(Date.now());

  const fnSave = useServerFn(saveAnswer);
  const fnAnsweredIds = useServerFn(getAttemptAnsweredIds);
  const fnSubmit = useServerFn(submitAttempt);
  const fnGenerateReport = useServerFn(generateBousalaReport);

  const total = queue.length;
  const item = queue[pos];

  useEffect(() => {
    setDraft(undefined);
    shownAt.current = Date.now();
  }, [item?.id]);

  useEffect(() => {
    if (phase === "preparing") preparingStartedAt.current = Date.now();
  }, [phase]);

  const heading = useScreenFocus<HTMLHeadingElement>(item?.id ?? phase);
  const questionLabelId = `bousala-repair-${item?.id ?? "none"}`;

  const t = useMemo(
    () =>
      lang === "en"
        ? {
            kicker: "Finishing your report",
            intro: "A few of your answers didn't reach us — that's on us, not you. Answer them here and your report continues from where it was.",
            counter: (i: number, n: number) => `${i} / ${n}`,
            remaining: (n: number) =>
              n === 1 ? "Only 1 question left" : `${n} questions left`,
            next: "Next",
            finish: "Finish and build my report",
            saveFailed: "We couldn't save that answer",
            retry: "Try again",
            stillTitle: "A few answers still need saving",
            stillBody: (n: number) => `${n} questions are still waiting. Let's finish them together.`,
            continue: "Continue",
          }
        : {
            kicker: "نُكمل تقريرك",
            intro: "بعض إجاباتك لم تصلنا بسبب خلل من طرفنا، لا من طرفك. أجب عنها هنا ويكمل تقريرك من حيث توقّف.",
            counter: (i: number, n: number) => `${i} / ${n}`,
            remaining: (n: number) => (n === 1 ? "بقي سؤال واحد فقط" : `بقيت ${n} أسئلة فقط`),
            next: "التالي",
            finish: "أنهِ وجهّز تقريري",
            saveFailed: "تعذّر حفظ الإجابة",
            retry: "أعد المحاولة",
            stillTitle: "بقيت بعض الإجابات بحاجة إلى حفظ",
            stillBody: (n: number) => `بقيت ${n} أسئلة. لنُنهها معًا.`,
            continue: "متابعة",
          },
    [lang],
  );

  async function verifyAndSubmit() {
    setPhase("verifying");
    try {
      // Re-read the server's answer set: never submit against what we only believe.
      const { ids } = await fnAnsweredIds({ data: { attemptId } });
      const answered = new Set<string>(ids);
      const gaps = orderMissing(
        SECTIONS.flatMap((s) => s.items.map((it) => it.id)).filter((id) => !answered.has(id)),
      ).filter((it) => !it.validity);
      if (gaps.length > 0) {
        setQueue(gaps);
        setPos(0);
        setStillMissing(gaps.length);
        setPhase("questions");
        return;
      }
      await fnSubmit({ data: { attemptId } });
      setPhase("preparing");
      void fnGenerateReport({ data: { attemptId } }).catch(() => {});
    } catch (e: any) {
      setPhase("questions");
      toast.error(userErrorMessage(e, lang, t.saveFailed));
    }
  }

  async function handleNext() {
    if (!item) return;
    const value = serializeAnswer(item, draft);
    if (value === null) return;
    setSaving(true);
    try {
      // Awaited, not fire-and-forget: these users are here because a save was lost.
      await fnSave({
        data: {
          attemptId,
          itemId: item.id,
          value,
          ms: Math.min(60 * 60 * 1000, Date.now() - shownAt.current),
          currentSection: item.section,
          currentIndex: 0,
        },
      });
      setStillMissing(null);
      if (pos + 1 >= total) {
        await verifyAndSubmit();
      } else {
        setPos((p) => p + 1);
      }
    } catch (e: any) {
      toast.error(userErrorMessage(e, lang, t.saveFailed));
    } finally {
      setSaving(false);
    }
  }

  if (phase === "preparing") {
    return (
      <div dir={dir}>
        <PreparingPhase
          lang={lang}
          attemptId={attemptId}
          startedAtMs={preparingStartedAt.current}
          onRetryScoring={async () => {
            await fnSubmit({ data: { attemptId } });
            void fnGenerateReport({ data: { attemptId } }).catch(() => {});
            onRefresh();
          }}
          onOpenReport={() => navigate({ to: "/portal/bousala/$attemptId", params: { attemptId } })}
          onContinueAnyway={navigateHome}
        />
      </div>
    );
  }

  if (phase === "verifying" || !item) {
    return (
      <div dir={dir} className="mx-auto max-w-2xl px-5 py-16 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  const itemLoc = tItem(item, lang);
  const sectionLoc = tSection(item.section, lang);
  const canProceed = serializeAnswer(item, draft) !== null;
  const labelSet: "likert5" | "importance5" = item.type === "importance5" ? "importance5" : "likert5";
  const remaining = total - pos;

  return (
    <div className="mx-auto max-w-2xl px-5 py-8 md:py-12" dir={dir}>
      <div className="rounded-ds-xl bg-accent/15 p-5 text-center">
        <p className="font-display text-sm font-extrabold text-foreground">{t.kicker}</p>
        <p className="mt-2 text-sm leading-relaxed text-brown-500">{t.intro}</p>
        <p className="mt-3 inline-flex rounded-full bg-primary/10 px-4 py-1.5 text-sm font-bold text-primary">
          {t.remaining(remaining)}
        </p>
      </div>

      {stillMissing !== null ? (
        <div className="mt-4 flex items-start gap-3 rounded-ds-lg bg-amber-100 p-4 text-start">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-800" aria-hidden="true" />
          <div>
            <p className="font-display text-sm font-bold text-foreground">{t.stillTitle}</p>
            <p className="mt-1 text-sm text-brown-500">{t.stillBody(stillMissing)}</p>
          </div>
        </div>
      ) : null}

      <div className="mt-6 rounded-ds-xl bg-surface p-6 shadow-sm md:p-8">
        <div className="flex items-center justify-between text-[13px] text-brown-500">
          <span className="font-bold">{sectionLoc.title}</span>
          <span>{t.counter(pos + 1, total)}</span>
        </div>

        <div
          className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={pos}
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ inlineSize: `${Math.round((pos / total) * 100)}%` }}
            aria-hidden="true"
          />
        </div>

        {item.type === "importance5" && itemLoc.stem ? (
          <p className="mt-4 text-sm font-bold text-brown-500">{itemLoc.stem}</p>
        ) : null}

        <h1
          id={questionLabelId}
          ref={heading}
          tabIndex={-1}
          className="mt-3 font-display text-lg font-bold leading-relaxed text-foreground outline-none md:text-xl"
        >
          {itemLoc.text}
        </h1>

        <div className="mt-6">
          {item.type === "likert5" || item.type === "importance5" ? (
            <LikertField
              lang={lang}
              item={item}
              labelledBy={questionLabelId}
              value={typeof draft === "number" ? (draft as number) : undefined}
              onChange={(v) => setDraft(v)}
              labelSet={labelSet}
            />
          ) : item.type === "scenario" || item.type === "pair" || item.type === "iq" ? (
            <ScenarioField
              lang={lang}
              item={item}
              labelledBy={questionLabelId}
              value={typeof draft === "string" ? (draft as string) : undefined}
              onChange={(id) => setDraft(id)}
            />
          ) : item.type === "top3" ? (
            <Top3Field
              lang={lang}
              item={item}
              labelledBy={questionLabelId}
              value={Array.isArray(draft) ? (draft as string[]) : undefined}
              onChange={(ids) => setDraft(ids)}
            />
          ) : null}
        </div>

        <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-8 flex items-center justify-between gap-3 rounded-b-ds-xl border-t border-hairline-subtle bg-surface/95 px-6 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 backdrop-blur md:static md:mx-0 md:mb-0 md:rounded-none md:border-0 md:bg-transparent md:px-0 md:pb-0 md:pt-0 md:backdrop-blur-none">
          <button
            type="button"
            onClick={navigateHome}
            className="inline-flex min-h-11 items-center gap-1 rounded-full border-2 border-border px-4 py-3 text-sm font-bold text-foreground/80 transition hover:bg-muted sm:px-5"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
            {lang === "en" ? "Later" : "لاحقًا"}
          </button>
          <button
            type="button"
            onClick={() => void handleNext()}
            disabled={!canProceed || saving}
            className={cn(
              "inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-sm transition sm:px-7",
              !canProceed || saving ? "cursor-not-allowed opacity-60" : "hover:opacity-95 active:scale-[0.99]",
            )}
          >
            {saving ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" /> : null}
            {pos + 1 >= total ? t.finish : t.next}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreparingPhase({
  lang,
  attemptId,
  startedAtMs,
  onRetryScoring,
  onOpenReport,
  onContinueAnyway,
}: {
  lang: Lang;
  attemptId: string;
  startedAtMs: number;
  onRetryScoring: () => Promise<void>;
  onOpenReport: () => void;
  onContinueAnyway: () => void;
}) {
  const fnGetReport = useServerFn(getBousalaReport);
  const { data, refetch } = useQuery({
    queryKey: ["bousala", "report", attemptId, "repair-preparing"],
    queryFn: () => fnGetReport({ data: { attemptId } }),
    refetchInterval: (q) => {
      const d = q.state.data as { status?: string; scoringError?: boolean } | undefined;
      if (d?.status === "report_ready") return false;
      if (d?.scoringError) return false;
      return 2000;
    },
    refetchOnWindowFocus: false,
  });
  const status = (data as { status?: string } | undefined)?.status ?? null;
  const scoringError = Boolean((data as { scoringError?: boolean } | undefined)?.scoringError);
  const [retrying, setRetrying] = useState(false);
  const [elapsed, setElapsed] = useState<number>(() => Date.now() - startedAtMs);
  useEffect(() => {
    const id = window.setInterval(() => setElapsed(Date.now() - startedAtMs), 500);
    return () => window.clearInterval(id);
  }, [startedAtMs]);
  return (
    <ReportPreparing
      lang={lang}
      status={status}
      elapsedMs={elapsed}
      onOpenReport={onOpenReport}
      onContinueAnyway={onContinueAnyway}
      hasError={scoringError}
      retrying={retrying}
      onRetry={async () => {
        setRetrying(true);
        try {
          await onRetryScoring();
        } catch {
          // surfaced by the refetch below
        } finally {
          setRetrying(false);
          void refetch();
        }
      }}
    />
  );
}

function BousalaRepairInShell() {
  return (
    <PublicShell ground="bg-cream-200">
      <BousalaRepair />
    </PublicShell>
  );
}
